/**
 * O campo Responsável da ficha (FR-32, FR-33) — quem é o dono desta conta.
 *
 * **"Responsável", e não "Pessoas".** São dois conceitos na mesma tela: aqui está a pessoa da
 * Orvalho que responde pela conta; no cartão Pessoas estão os contatos dentro da Empresa.
 *
 * Desde o realinhamento ao protótipo v2 (28/09/2026) ele **mora na grade do primeiro cartão da
 * ficha**, ao lado de Categoria e Setor, como no protótipo — e não mais num cartão próprio. A
 * ficha o recebe pela prop `responsavel`, só na tela de edição: na criação o Responsável é quem
 * cadastra (a distribuição é sempre humana, FR-33), e o formulário mostra um campo somente
 * leitura no mesmo lugar.
 *
 * **Campo próprio, e não um campo do rascunho.** O `PUT` da ficha é corpo completo e fechado
 * (`extra="forbid"`), e `responsavel_id` não está nele: aceitá-lo ali faria a entrega acontecer
 * sem a trilha própria que o FR-32 exige, escondida no meio de uma edição de ficha. Como campo
 * próprio, a troca é um ato só, com a sua confirmação e a sua recusa — logo abaixo do controle.
 *
 * **A escolha grava na hora.** Não há botão "Salvar" porque não há rascunho: o ato é escolher
 * uma pessoa. Um botão à parte deixaria a tela mostrando um dono que o servidor não tem.
 *
 * **O que está selecionado tem duas fontes, e nenhuma delas é um estado local.** Enquanto a
 * gravação está em voo, o valor exibido é o **escolhido** (`gravacao.variables`); terminada a
 * gravação, a fonte volta a ser a ficha do servidor — e é isso que faz a recusa se resolver
 * sozinha, sem um `setState` de reversão que alguém poderia esquecer no próximo caminho de erro.
 *
 * **Ficha órfã e Responsável desativado são dois casos, e os dois aparecem.** A órfã mostra
 * "Sem Responsável" como estado corrente, e essa opção **não é escolhível**: orfanar é ato do
 * desligamento (2.7), em contexto de sistema, e não tem porta de usuário (FR-34). O desativado
 * aparece **nomeado e marcado** (FR-65 manda o nome do desligado continuar no histórico), e
 * também não entra na lista de escolhas novas — quem saiu não recebe conta nova.
 *
 * **Esconder não é autorizar.** O controle aparece para quem é **dono** da ficha — a Responsável ou
 * a gestão (`quem_le_e_o_dono` da resposta, 13.14b, DW-438) —, qualquer que seja o perfil, e a
 * permissão `trocar_responsavel` é concedida aos quatro (FR-32). Quem só vê a ficha pela PA-1, pela
 * PA-3 ou pelo Grupo lê o Responsável em texto, com a explicação: o serviço recusaria a troca (o
 * gatilho `empresa_reserva_o_dono`). Quem recusa continua sendo o serviço, e a mensagem dele vai à
 * tela **verbatim**.
 *
 * **Depois de entregar a ficha que não se vê mais** (13.14a, DW-508): quem não é gestão e entrega a
 * Empresa a outra pessoa relê a ficha — fora da chave da tela, para nada piscar a recusa. `404` (ela
 * saiu da Carteira, e nem a PA-1 nem o Grupo a mostram): a tela sai para Empresas (`replace`), esquece
 * a ficha e anuncia "Empresa entregue a Fulana. Ela saiu da sua lista." pela região de avisos do
 * layout. Ainda a vê, ou a releitura falha por rede: fica, com "Responsável salvo.".
 *
 * **Paridade de celular (NFR-1).** Um `<select>` nativo, que o celular já desenha como roda e o
 * teclado percorre com as setas; sem árvore alternativa e sem `@media`.
 */

import { useEffect, useRef } from 'react'
import { useNavigate } from '@tanstack/react-router'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { type Responsavel, useResponsaveis } from '@/modulos/identidade/api'
import { anunciar } from '@/modulos/negocios/avisos'
import { CAMINHOS } from '@/shell/navegacao'
import { type Empresa, lerEmpresa, useEmpresa, useEsquecerAEmpresa, useTrocarResponsavel } from './api'
import './troca-de-responsavel.css'

/** `id` do rótulo — é ele que nomeia o grupo e o `<select>` para o leitor de tela. */
const ID_DO_ROTULO = 'responsavel-rotulo'
const ID_DO_CAMPO = 'responsavel-campo'
const ID_DA_AJUDA = 'responsavel-ajuda'

const ROTULO = 'Responsável'

/** A nota de auditoria, como ajuda pequena abaixo do campo (protótipo v2). */
export const AJUDA =
  'A troca é registrada com autor, data e hora, e move a Empresa de uma Carteira para a outra.'

const CARREGANDO = 'Carregando o Responsável…'
const SALVANDO = 'Salvando…'
const SALVO = 'Responsável salvo.'

/** O que a ficha órfã mostra. Não é uma escolha: ninguém entrega uma Empresa a ninguém. */
const SEM_RESPONSAVEL = 'Sem Responsável'

/** O sufixo de quem já não está na equipe. O nome continua; a escolha, não (FR-65). */
const DESATIVADO = ' (desativado)'

/** Por que quem não é dono vê o Responsável sem o seletor (13.14b, DW-438). */
export const SO_O_DONO_TROCA = 'Só o Responsável pela empresa ou a gestão trocam o Responsável.'

/** O aviso de depois da entrega da ficha que saiu da lista de quem entregou (13.14a, DW-508). */
export function avisoDaEntrega(nomeDoNovo: string | undefined): string {
  return nomeDoNovo === undefined
    ? 'Empresa entregue. Ela saiu da sua lista.'
    : `Empresa entregue a ${nomeDoNovo}. Ela saiu da sua lista.`
}

/** O valor de `<option>` da ficha órfã — string vazia, porque `null` não cabe num `select`. */
const VALOR_VAZIO = ''

/** A mensagem do serviço quando ela existe; a genérica quando a falha nem chegou a ter uma. */
function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/** O nome como a tela o mostra: o do desativado vem marcado, e não escondido (FR-65). */
function nomeDe(pessoa: Responsavel): string {
  return pessoa.ativo ? pessoa.nome : `${pessoa.nome}${DESATIVADO}`
}

export interface PropriedadesDaTrocaDeResponsavel {
  /** A Empresa dona da conta. O campo só existe na tela de edição, então ela sempre existe. */
  empresaId: string
}

export function TrocaDeResponsavel({ empresaId }: PropriedadesDaTrocaDeResponsavel) {
  // A **mesma** chave que o formulário da ficha já lê: nenhuma consulta nova sai da tela por
  // causa deste campo, e a resposta da mutação atualiza os dois de uma vez.
  const consulta = useEmpresa(empresaId)
  // A equipe tem chave própria e compartilhada (a 2.6 lê a mesma): ela muda raramente, e uma
  // consulta por ficha aberta seria uma ida à rede para receber sempre o mesmo.
  const equipe = useResponsaveis()
  const gravacao = useTrocarResponsavel(empresaId)
  const navegar = useNavigate()
  const esquecer = useEsquecerAEmpresa()
  /** A ficha ainda está aberta? A releitura volta depois; quem já saiu dela não é levado embora. */
  const montado = useRef(true)
  useEffect(() => {
    montado.current = true
    return () => {
      montado.current = false
    }
  }, [])

  /** 13.14a (DW-508): a ficha entregue ainda é de quem entregou? Sem ela, sai para a lista. */
  async function conferirDepoisDaEntrega(entregue: Empresa) {
    //: 13.14b: quem continua dono depois da entrega diz a resposta (a gestão, sim).
    if (entregue.quem_le_e_o_dono) {
      return
    }
    try {
      await lerEmpresa(empresaId)
      return
    } catch (falha) {
      if (!(falha instanceof ErroDaApi) || falha.status !== 404) {
        return
      }
    }
    if (!montado.current) {
      return
    }
    const novo = (equipe.data ?? []).find((pessoa) => pessoa.id === entregue.responsavel_id)
    //: Sai da ficha **antes** de esquecê-la: montada, ela releria a Empresa que saiu.
    await navegar({ to: CAMINHOS.empresas, replace: true })
    esquecer(empresaId)
    anunciar(avisoDaEntrega(novo?.nome))
  }

  const pessoas = equipe.data ?? []
  // Enquanto grava, o que está selecionado é o que está **em voo**; depois, o que o servidor
  // tem. `gravacao.variables` sobrevive à mutação, então só vale sob `isPending` — fora daí ele
  // diria que a escolha recusada foi gravada.
  const atual = gravacao.isPending ? gravacao.variables : consulta.data?.responsavel_id

  // A ficha existe mas ainda não sabemos quem é o dono: `undefined` é "não chegou", e `null` é
  // "não tem" — dois estados diferentes, e desenhar um pelo outro diria que a conta é órfã.
  const fichaChegou = atual !== undefined
  const dono = fichaChegou ? pessoas.find((pessoa) => pessoa.id === atual) : undefined
  // O Responsável que a lista não conhece: a equipe não carregou, ou o uuid não é de ninguém.
  // Mostrar o identificador é pior que nada? Não: é o que o servidor tem, e escondê-lo faria a
  // tela parecer órfã — que é exatamente o estado que a gestão precisa distinguir para
  // redistribuir.
  const donoDesconhecido = fichaChegou && atual !== null && dono === undefined

  // Só quem está ativo é oferecido; o dono atual entra de qualquer jeito, para que o `select`
  // tenha o valor que ele está exibindo. Sem isso um Responsável desativado apareceria como se
  // outra pessoa fosse a dona da conta.
  const escolhiveis = pessoas.filter((pessoa) => pessoa.ativo || pessoa.id === atual)
  /* 13.14b (DW-438): sem o seletor para quem não é dono — a ficha que chegou diz. */
  const soLeitura = consulta.data !== undefined && !consulta.data.quem_le_e_o_dono
  const nomeLido =
    atual === null || atual === undefined
      ? SEM_RESPONSAVEL
      : dono !== undefined
        ? nomeDe(dono)
        : equipe.isLoading
          ? CARREGANDO
          : atual

  function escolher(valor: string) {
    // A guarda é o que impede a segunda gravação, e por isso o `select` **não** é desabilitado:
    // um controle desabilitado perde o foco do teclado para o `<body>` no exato momento em que
    // a pessoa acabou de usá-lo. `VALOR_VAZIO` é o "Sem Responsável" da ficha órfã, que existe
    // para ser mostrado e não para ser escolhido (FR-34).
    if (valor === VALOR_VAZIO || valor === atual || gravacao.isPending) {
      return
    }
    gravacao.mutate(valor, {
      onSuccess: (entregue) => {
        void conferirDepoisDaEntrega(entregue)
      },
    })
  }

  return (
    /* Um `group` nomeado pelo próprio rótulo: é o bloco que o leitor de tela e a suíte
       alcançam — o campo, a ajuda, o "Salvando…" e a recusa moram todos aqui. */
    <div className="empresa__campo responsavel" role="group" aria-labelledby={ID_DO_ROTULO}>
      <label
        className="empresa__rotulo"
        htmlFor={soLeitura ? undefined : ID_DO_CAMPO}
        id={ID_DO_ROTULO}
      >
        {ROTULO}
      </label>
      {consulta.isError ? (
        /* Verbatim: o `404` do serviço é o mesmo para ficha inexistente e para ficha que a
           RLS esconde (FR-30), e é ele que sabe explicar o caso. */
        <p className="empresa__erro" role="alert">
          {mensagemDe(consulta.error)}
        </p>
      ) : /* `isLoading`, e não `isPending`: uma consulta desabilitada fica `pending` para
             sempre no React Query v5, e o campo travaria em "Carregando…" sem nunca ter saído
             nenhuma requisição. */
      consulta.isLoading || !fichaChegou ? (
        /* Sem ficha não há dono: desenhar o seletor com a primeira pessoa da equipe marcada
           diria, por um instante, que a conta é de quem ela não é. */
        <p className="empresa__ajuda" role="status">
          {CARREGANDO}
        </p>
      ) : soLeitura ? (
        <>
          <p className="responsavel__lido">
            {nomeLido}
          </p>
          <span className="empresa__ajuda">{SO_O_DONO_TROCA}</span>
        </>
      ) : (
        <>
          <select
            id={ID_DO_CAMPO}
            className="empresa__selecao responsavel__selecao"
            value={atual ?? VALOR_VAZIO}
            aria-busy={gravacao.isPending ? true : undefined}
            aria-describedby={ID_DA_AJUDA}
            onChange={(evento) => {
              escolher(evento.target.value)
            }}
          >
            {/* A ficha órfã precisa de uma linha para exibir o próprio estado. Ela é `disabled`
                porque não é uma escolha: não há caminho de usuário para devolver uma Empresa ao
                limbo (FR-34). */}
            {atual === null ? (
              <option value={VALOR_VAZIO} disabled>
                {SEM_RESPONSAVEL}
              </option>
            ) : null}
            {/* O dono que a lista não conhece: o `select` precisa da linha, senão ele cairia
                calado na primeira opção e mostraria outra pessoa. */}
            {donoDesconhecido ? <option value={atual}>{atual}</option> : null}
            {escolhiveis.map((pessoa) => (
              <option key={pessoa.id} value={pessoa.id}>
                {nomeDe(pessoa)}
              </option>
            ))}
          </select>
          {equipe.isError ? (
            /* A ficha carregou e a equipe não: o valor atual continua correto, e o que falta é
               poder escolher outro. A recusa vai verbatim, como as demais. */
            <p className="empresa__erro" role="alert">
              {mensagemDe(equipe.error)}
            </p>
          ) : null}
        </>
      )}
      <span className="empresa__ajuda" id={ID_DA_AJUDA}>
        {AJUDA}
      </span>

      {gravacao.isError ? (
        <p className="empresa__erro" role="alert">
          {mensagemDe(gravacao.error)}
        </p>
      ) : null}
      {/* `role="status"` e não `role="alert"`: deu certo, e o leitor de tela não precisa
          interromper o que a pessoa estiver fazendo para dizer isso. */}
      {gravacao.isPending ? (
        <p className="empresa__ajuda" role="status">
          {SALVANDO}
        </p>
      ) : gravacao.isSuccess ? (
        <p className="empresa__sucesso" role="status">
          {SALVO}
        </p>
      ) : null}
    </div>
  )
}
